import React, { useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import CollectionPointMapComponent from '@/components/collectionPoint/map/CollectionPointMap.jsx';
import CollectionPointMapFilters from '@/components/collectionPoint/map/CollectionPointMapFilters.jsx';
import { useCollectionPointMapPoints } from '@/components/collectionPoint/map/useCollectionPointMapPoints';
import { Palette } from '@/themes/themeOverrides/Palette';

export const CollectionPointMap = () => {
    const { isLoading } = usePermissions();
    const translate = useTranslate();
    const google_maps_api_key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const MAP_ID = 'ef998239259c4672dac8d1bc';
    const [selection, setSelection] = useState(null);
    const { points: collectionPointData, isInitialLoading, isLoadingMore, error } = useCollectionPointMapPoints();

    if (isLoading || isInitialLoading) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                <CircularProgress />
            </Box>
        );
    }

    if (error && collectionPointData.length === 0) {
        return (
            <Box p={2}>
                <Alert severity='error'>{translate('common.map.collectionPointLoadError', { message: error.message })}</Alert>
            </Box>
        );
    }

    if (!collectionPointData || collectionPointData.length === 0) {
        if (isLoadingMore) {
            return (
                <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <Box p={2}>
                <Alert severity='info'>{translate('common.map.noCollectionPointData')}</Alert>
            </Box>
        );
    }

    const devicesCount = collectionPointData.reduce((total, collectionPoint) => {
        return total + (Array.isArray(collectionPoint.devices) ? collectionPoint.devices.length : 0);
    }, 0);
    const showDevicesCount = collectionPointData.some(collectionPoint => Array.isArray(collectionPoint.devices));

    const isFullyLoaded = !isLoadingMore && !error;

    return (
        <Box>
            {error && (
                <Box mb={2}>
                    <Alert severity='error'>{translate('common.map.collectionPointLoadError', { message: error.message })}</Alert>
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    width: '100%',
                    marginBottom: '32px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '16px',
                    }}
                >
                    <Typography
                        variant='h3'
                        sx={{
                            fontWeight: '400',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        {translate('menu.locations')}:{isLoadingMore && <CircularProgress size={16} sx={{ color: Palette.black }} />}
                        <Box component='span' sx={{ fontWeight: '700' }}>
                            {collectionPointData.length}
                        </Box>
                    </Typography>
                    {showDevicesCount && (
                        <Typography variant='caption' fontSize='14px' gutterBottom>
                            {translate('common.map.including')}{' '}
                            <Box component='span' sx={{ fontWeight: '600' }}>
                                {devicesCount}
                            </Box>{' '}
                            {translate('common.importSection.devicesGenitive')}
                        </Typography>
                    )}
                </Box>
                <CollectionPointMapFilters
                    collectionPointData={collectionPointData}
                    selection={selection}
                    onSelectionChange={setSelection}
                    disabled={!isFullyLoaded}
                />
            </Box>

            <CollectionPointMapComponent
                collectionPointData={collectionPointData}
                googleMapsApiKey={google_maps_api_key}
                mapId={MAP_ID}
                selection={selection}
                isLoadingMore={isLoadingMore}
            />
        </Box>
    );
};

export default CollectionPointMap;
